import React, { useCallback, useEffect, useState } from 'react';
import theme from '../../theme';
import { createPortal } from 'react-dom';
import { fetchHitHistory, fetchHitLogs } from '../../services/api';
import { accentFor } from './NotificationPanel';

const LEVEL_LETTER = { t: 'T', m: 'M', s: 'S' };

const fmtTime = (iso) => {
  if (!iso) return '';
  // Stored as naive UTC.
  const d = new Date(/Z|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const sideTone = (side) => ((side || '').toLowerCase() === 'buy'
  ? { chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200', node: 'bg-emerald-500' }
  : { chip: 'bg-rose-50 text-rose-700 ring-rose-200', node: 'bg-rose-500' });

const lineTone = (line) => (
  / (ERROR|CRITICAL) /.test(line) ? 'text-rose-300'
    : / WARNING /.test(line) ? 'text-amber-300'
      : 'text-olive-100'
);

// Full console log of one hit — fetched only when opened.
function HitLogsModal({ hit, load, onClose }) {
  const [state, setState] = useState({ loading: true, logs: null, error: false });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    load().then((res) => alive && setState({ loading: false, logs: res.logs, error: !!res.error }));
    return () => { alive = false; };
  }, [load]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.logs || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { void e; }
  };

  const lines = state.logs ? state.logs.split('\n') : [];
  return createPortal(
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme.modalOverlay}`} onClick={onClose}>
      <div
        role="dialog"
        aria-label="Hit logs"
        className={`${theme.modalBg} flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-olive-100 px-4 py-3">
          <span className={`rounded-md px-1.5 py-0.5 font-mono text-[12px] font-semibold ring-1 ${sideTone(hit.side).chip}`}>
            {hit.timeframe_meta} · {hit.wave_from || '—'} → {hit.wave_to}
          </span>
          <span className={`text-xs ${theme.textMuted}`}>{fmtTime(hit.timestamp)} · {lines.length} lines</span>
          <button type="button" onClick={copy} disabled={!state.logs}
                  className={`ml-auto rounded-lg px-2 py-1 text-xs font-medium disabled:opacity-30 ${theme.btnSecondary}`}>
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button type="button" onClick={onClose} aria-label="Close"
                  className={`rounded-lg px-2 py-1 text-xs font-medium ${theme.btnGhost}`}>
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-olive-950 p-3">
          {state.loading ? (
            <p className="text-xs text-olive-300">Loading…</p>
          ) : !state.logs ? (
            <p className="text-xs text-olive-300">
              {state.error ? 'Could not load the log.' : 'The log of this hit has expired.'}
            </p>
          ) : (
            <pre className="whitespace-pre-wrap break-all font-mono text-[11px] leading-relaxed">
              {lines.map((line, i) => <div key={i} className={lineTone(line)}>{line}</div>)}
            </pre>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function HitCard({ hit, fresh, onDetails }) {
  const tone = sideTone(hit.side);
  return (
    <li className="relative pl-6 pb-3">
      <span className={`absolute left-[7px] top-2 h-2.5 w-2.5 rounded-full ring-2 ring-white ${tone.node}`} aria-hidden="true" />
      <div data-testid="hit" className={`rounded-xl border border-olive-100 px-2.5 py-2 transition-colors duration-1000 ${fresh ? 'bg-amber-50' : 'bg-white'}`}>
        <div className="flex items-center gap-1.5">
          <span className={`rounded-md px-1.5 py-0.5 font-mono text-[12px] font-semibold ring-1 ${tone.chip}`}>
            {hit.wave_from || '—'} → {hit.wave_to}
          </span>
          <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${theme.badge}`}>
            {LEVEL_LETTER[(hit.source_name || '').toLowerCase()] || hit.source_name}
          </span>
          <span className={`text-[11px] font-medium ${theme.textSecondary}`}>{(hit.side || '').toUpperCase()}</span>
          <span className={`ml-auto text-[11px] ${theme.textMuted}`}>{fmtTime(hit.timestamp)}</span>
          <button
            type="button"
            onClick={() => onDetails(hit)}
            className="rounded-md px-1.5 py-0.5 text-[11px] font-medium text-olive-500 hover:bg-olive-50 hover:text-olive-700"
          >
            Details
          </button>
        </div>
        {hit.events?.length ? (
          <ul className="mt-1.5 space-y-1 border-t border-olive-50 pt-1.5">
            {hit.events.map((ev, i) => (
              <li key={i} className="flex gap-2">
                <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${accentFor(ev).dot}`} aria-hidden="true" />
                <span className={`text-[12px] leading-snug ${ev.type === 'error' ? 'text-rose-700' : theme.textSecondary}`}>
                  {ev.message}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={`mt-1 text-[11px] italic ${theme.textMuted}`}>No follow-up events</p>
        )}
      </div>
    </li>
  );
}

// History of one real timeframe: each hit that moved its wave, and what it caused.
export default function HitTimeline({ title, tabs, asset, account, websocket, favs = [], toggleFav = () => {} }) {
  const [timeframe, setTimeframe] = useState(null);
  const [timeframes, setTimeframes] = useState([]);
  // Favourites first, each group keeps its recency order.
  const ordered = [...timeframes.filter((tf) => favs.includes(tf)), ...timeframes.filter((tf) => !favs.includes(tf))];
  const [hits, setHits] = useState([]);
  const [fresh, setFresh] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const loadLogs = useCallback(
    () => fetchHitLogs(account, asset.broker, asset.symbol, details?.hit_id),
    [account, asset, details],
  );

  useEffect(() => { setTimeframe(null); }, [asset?.symbol, asset?.broker, account]);

  const load = useCallback(async () => {
    if (!asset || !account) return;
    setLoading(true);
    const res = await fetchHitHistory(account, asset.broker, asset.symbol, timeframe);
    setTimeframes(res.timeframes || []);
    setHits(res.hits || []);
    if (!timeframe && res.timeframe_meta) setTimeframe(res.timeframe_meta);
    setLoading(false);
  }, [asset, account, timeframe]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!websocket) return undefined;
    const onMessage = (event) => {
      if (event.data === 'ping') return;
      let msg;
      try { msg = JSON.parse(event.data); } catch (e) { return; }
      if (msg.type !== 'hit_update' || !msg.hit) return;
      const hit = msg.hit;
      setTimeframes((prev) => [hit.timeframe_meta, ...prev.filter((tf) => tf !== hit.timeframe_meta)]);
      setTimeframe((cur) => cur || hit.timeframe_meta);
      if (timeframe && hit.timeframe_meta !== timeframe) return;
      setHits((prev) => (prev.some((h) => h.hit_id === hit.hit_id) ? prev : [hit, ...prev]));
      setFresh((prev) => new Set(prev).add(hit.hit_id));
      setTimeout(() => setFresh((prev) => {
        const next = new Set(prev);
        next.delete(hit.hit_id);
        return next;
      }), 4000);
    };
    websocket.addEventListener('message', onMessage);
    return () => websocket.removeEventListener('message', onMessage);
  }, [websocket, timeframe]);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-olive-100 px-3 py-2.5">
        <div className="min-w-0">
          <p className={`truncate text-sm font-semibold ${theme.textPrimary}`}>{title}</p>
          <p className={`text-[11px] ${theme.textMuted}`}>
            {timeframe ? `${hits.length} hits · ${timeframe}` : 'wave transitions per timeframe'}
          </p>
        </div>
        {tabs}
      </div>

      {timeframes.length > 0 && (
        <div className="px-3 pt-2">
          <div className={`flex gap-1 overflow-x-auto ${theme.pillContainer}`}>
            {ordered.map((tf) => (
              <span key={tf} className={`flex shrink-0 items-center rounded-lg transition-colors ${tf === timeframe ? theme.pillActive : theme.pillInactive}`}>
                <button type="button" onClick={() => setTimeframe(tf)} className="py-1 pl-2.5 pr-1 text-xs font-medium">
                  {tf}
                </button>
                <button
                  type="button"
                  onClick={() => toggleFav(tf)}
                  aria-label={favs.includes(tf) ? `Unfavourite ${tf}` : `Favourite ${tf}`}
                  title={favs.includes(tf) ? 'Remove from favourites' : 'Add to favourites'}
                  className={`py-1 pr-2 text-xs ${favs.includes(tf) ? 'text-amber-500' : 'text-olive-300 hover:text-amber-400'}`}
                >
                  {favs.includes(tf) ? '★' : '☆'}
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 max-h-72 overflow-y-auto px-3">
        {loading && hits.length === 0 ? (
          <p className={`py-6 text-center text-sm ${theme.textMuted}`}>Loading…</p>
        ) : hits.length === 0 ? (
          <p className={`py-6 text-center text-sm ${theme.textMuted}`}>No wave transitions yet.</p>
        ) : (
          <div className="relative">
            <span className="absolute left-[11px] top-2 bottom-3 w-px bg-olive-100" aria-hidden="true" />
            <ol>
              {hits.map((hit) => (
                <HitCard key={hit.hit_id} hit={hit} fresh={fresh.has(hit.hit_id)} onDetails={setDetails} />
              ))}
            </ol>
          </div>
        )}
      </div>
      {details && <HitLogsModal hit={details} load={loadLogs} onClose={() => setDetails(null)} />}
    </div>
  );
}
